import { LARGE_FILE_THRESHOLD, MAX_CHUNK_SIZE, MIN_CHUNK_SIZE } from '../../../../config/consts';
import { useCommitUploadSession } from '../../../../context/documents/useCommitUploadSession';
import { useStartNewDocumentUploadSession } from '../../../../context/documents/useStartNewDocumentUploadSession';
import { useStartUploadSession } from '../../../../context/documents/useStartUploadSession';
import { useUploadChunk } from '../../../../context/documents/useUploadChunk';
import { GearDocumentMetadata } from '../../../../Model/GearDocumentMetadata';

interface UseChunkedUploadParams {
  selectedIndexId: number;
  isNewDocument: boolean;
}

interface ChunkedUploadParams {
  file: File;
  ctName: string;
  documentId?: string;
  metadata: GearDocumentMetadata;
}

export const useChunkedUpload = ({ selectedIndexId, isNewDocument }: UseChunkedUploadParams) => {
  const { mutateAsync: startUploadSession } = useStartUploadSession();
  const { mutateAsync: startNewDocumentUploadSession } = useStartNewDocumentUploadSession();
  const { mutateAsync: uploadChunk } = useUploadChunk();
  const { mutateAsync: commitUploadSession } = useCommitUploadSession();

  const calculateChunkSize = (fileSize: number): number => {
    let chunkSize = fileSize / 10;
    chunkSize = Math.min(chunkSize, MAX_CHUNK_SIZE);
    chunkSize = Math.max(chunkSize, MIN_CHUNK_SIZE);
    chunkSize = Math.round(chunkSize / (1024 * 1024)) * 1024 * 1024;
    return chunkSize;
  };

  const createFileChunk = (file: File, start: number, end: number): File => {
    const blobChunk = file.slice(start, end);
    return new File([blobChunk], file.name, { type: file.type, lastModified: file.lastModified });
  };

  const startFileUploadSession = async (
    file: File,
    ctName: string,
    chunkSize: number,
    documentId?: string
  ): Promise<string> => {
    const firstChunkStart = 0;
    const firstChunkEnd = Math.min(chunkSize, file.size);
    const firstChunk = createFileChunk(file, firstChunkStart, firstChunkEnd);

    if (isNewDocument) {
      const sessionResponse = await startNewDocumentUploadSession({
        dto: {
          ctName,
          searchIndexId: selectedIndexId,
          chunk: {
            chunkIndex: 0,
            file: firstChunk
          }
        }
      });
      return sessionResponse.sessionId;
    } else {
      if (!documentId) {
        throw new Error('Document ID is required for existing document upload');
      }

      const sessionResponse = await startUploadSession({
        dto: {
          indexId: selectedIndexId,
          ctName,
          documentId,
          chunk: {
            chunkIndex: 0,
            file: firstChunk
          }
        }
      });
      return sessionResponse.sessionId;
    }
  };

  const uploadRemainingChunks = async (
    file: File,
    sessionId: string,
    ctName: string,
    chunkSize: number,
    totalChunks: number,
    metadata: GearDocumentMetadata
  ): Promise<void> => {
    let lastChunkIndex = 0;

    for (let i = 1; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = createFileChunk(file, start, end);

      await uploadChunk({
        dto: {
          ctName,
          indexId: selectedIndexId,
          sessionId,
          chunk: {
            chunkIndex: i,
            file: chunk
          }
        },
        totalChunks
      });

      lastChunkIndex = i;
    }

    await commitUploadSession({
      dto: {
        indexId: selectedIndexId,
        ctName,
        lastChunkIndex,
        fileName: file.name,
        sessionId,
        metadata
      }
    });
  };

  const uploadLargeFile = async ({ file, ctName, documentId, metadata }: ChunkedUploadParams): Promise<void> => {
    const chunkSize = calculateChunkSize(file.size);
    const totalChunks = Math.ceil(file.size / chunkSize);
    const sessionId = await startFileUploadSession(file, ctName, chunkSize, documentId);
    await uploadRemainingChunks(file, sessionId, ctName, chunkSize, totalChunks, metadata);
  };

  const isLargeFile = (fileSize: number): boolean => {
    return fileSize > LARGE_FILE_THRESHOLD;
  };

  return {
    uploadLargeFile,
    isLargeFile,
    isLoading: false
  };
};
